/**
 * Project Drive Sync Service
 *
 * Handles bidirectional sync between project files and Google Drive.
 * Each project has its own folder in Drive, named with the project ID.
 */

import type { ProjectFile, CloudProvider } from '../domain/models';
import type { GoogleDriveFile } from '../domain/google';
import {
  listDriveFiles,
  createDriveFolder,
  getDriveFile,
  trashDriveFile,
  searchDriveFiles,
} from './googleDriveClient';
import { genId } from '../domain/idUtils';

/** Root folder name in Drive for all project files */
const ROOT_FOLDER_NAME = 'Business Assistant Projects';

/** Cache for the root folder ID */
let rootFolderId: string | null = null;

/**
 * Get or create the root folder for all project files in Drive
 */
export async function getOrCreateRootFolder(sessionId: string): Promise<string> {
  if (rootFolderId) {
    return rootFolderId;
  }

  // Search for existing root folder
  const existingFolders = await searchDriveFiles(sessionId, ROOT_FOLDER_NAME, {
    type: 'folder',
    pageSize: 5,
  });

  const rootFolder = existingFolders.find(
    (f) => f.name === ROOT_FOLDER_NAME && f.mimeType === 'application/vnd.google-apps.folder'
  );

  if (rootFolder) {
    rootFolderId = rootFolder.id;
    return rootFolder.id;
  }

  // Create root folder
  const newFolder = await createDriveFolder(sessionId, ROOT_FOLDER_NAME);
  rootFolderId = newFolder.id;
  return newFolder.id;
}

/**
 * Get or create a project folder in Drive
 * Folder name: "{projectTitle} ({projectId})"
 */
export async function getOrCreateProjectFolder(
  sessionId: string,
  projectId: string,
  projectTitle: string,
  existingFolderId?: string
): Promise<{ folderId: string; created: boolean }> {
  // If we already have a folder ID, verify it exists
  if (existingFolderId) {
    try {
      await getDriveFile(sessionId, existingFolderId);
      return { folderId: existingFolderId, created: false };
    } catch {
      // Folder was deleted, create a new one
    }
  }

  // Get root folder
  const rootId = await getOrCreateRootFolder(sessionId);

  // Search for existing project folder
  const folderName = `${projectTitle} (${projectId})`;
  const { files } = await listDriveFiles(sessionId, {
    query: `'${rootId}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`,
    pageSize: 100,
  });

  const existingFolder = files.find(
    (f) => f.name.includes(projectId) && f.mimeType === 'application/vnd.google-apps.folder'
  );

  if (existingFolder) {
    return { folderId: existingFolder.id, created: false };
  }

  // Create new project folder
  const newFolder = await createDriveFolder(sessionId, folderName, rootId);
  return { folderId: newFolder.id, created: true };
}

/**
 * Convert a Drive file to a ProjectFile
 */
export function driveFileToProjectFile(
  driveFile: GoogleDriveFile,
  projectId: string,
  uploadedBy: string = 'usr-system'
): ProjectFile {
  const now = new Date().toISOString();

  return {
    id: genId('fil'),
    projectId,
    name: driveFile.name,
    mimeType: driveFile.mimeType,
    size: parseInt(driveFile.size || '0', 10),
    provider: 'google-drive',
    externalUrl: driveFile.webViewLink || `https://drive.google.com/file/d/${driveFile.id}/view`,
    externalId: driveFile.id,
    thumbnailUrl: driveFile.thumbnailLink,
    uploadedBy,
    createdAt: driveFile.createdTime || now,
    updatedAt: driveFile.modifiedTime || now,
  };
}

/**
 * List all files in a project's Drive folder
 */
export async function listProjectDriveFiles(
  sessionId: string,
  folderId: string
): Promise<GoogleDriveFile[]> {
  const allFiles: GoogleDriveFile[] = [];
  let pageToken: string | undefined;

  do {
    const { files, nextPageToken } = await listDriveFiles(sessionId, {
      query: `'${folderId}' in parents and trashed=false`,
      pageSize: 100,
      pageToken,
    });

    allFiles.push(...files);
    pageToken = nextPageToken;
  } while (pageToken);

  return allFiles;
}

/**
 * Sync files from Drive to local state
 * Returns files that exist in Drive but not locally
 */
export async function syncFromDrive(
  sessionId: string,
  folderId: string,
  projectId: string,
  localFiles: ProjectFile[],
  uploadedBy: string
): Promise<{
  newFiles: ProjectFile[];
  deletedFileIds: string[];
}> {
  // Get all files from Drive
  const driveFiles = await listProjectDriveFiles(sessionId, folderId);

  // Map local files by externalId
  const localByExternalId = new Map(
    localFiles
      .filter((f) => f.externalId)
      .map((f) => [f.externalId!, f])
  );

  // Find new files (in Drive but not locally)
  const newFiles: ProjectFile[] = [];
  const driveFileIds = new Set<string>();

  for (const driveFile of driveFiles) {
    driveFileIds.add(driveFile.id);

    if (!localByExternalId.has(driveFile.id)) {
      // New file from Drive
      newFiles.push(driveFileToProjectFile(driveFile, projectId, uploadedBy));
    }
  }

  // Find deleted files (locally but not in Drive)
  const deletedFileIds: string[] = [];
  for (const localFile of localFiles) {
    if (localFile.externalId && !driveFileIds.has(localFile.externalId)) {
      deletedFileIds.push(localFile.id);
    }
  }

  return { newFiles, deletedFileIds };
}

/**
 * Delete a file from Drive
 */
export async function deleteFromDrive(
  sessionId: string,
  externalId: string
): Promise<void> {
  await trashDriveFile(sessionId, externalId);
}

/**
 * Get upload URL for a project folder
 * This returns the Drive folder URL where users can upload files directly
 */
export function getProjectFolderUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${folderId}`;
}

/**
 * Check if a file still exists in Drive
 */
export async function fileExistsInDrive(
  sessionId: string,
  externalId: string
): Promise<boolean> {
  try {
    const file = await getDriveFile(sessionId, externalId);
    return !file.trashed;
  } catch {
    return false;
  }
}

/**
 * Determine sync status for a project
 */
export interface SyncStatus {
  isSynced: boolean;
  lastSyncAt?: string;
  localCount: number;
  driveCount: number;
  pendingUploads: number;
  pendingDeletes: number;
}

export async function getProjectSyncStatus(
  sessionId: string,
  folderId: string,
  localFiles: ProjectFile[],
  lastSyncAt?: string
): Promise<SyncStatus> {
  const driveFiles = await listProjectDriveFiles(sessionId, folderId);

  const localByExternalId = new Map(
    localFiles
      .filter((f) => f.externalId)
      .map((f) => [f.externalId!, f])
  );

  const driveFileIds = new Set(driveFiles.map((f) => f.id));

  // Files in Drive but not locally
  const pendingUploads = driveFiles.filter((f) => !localByExternalId.has(f.id)).length;

  // Files locally but not in Drive
  const pendingDeletes = localFiles.filter(
    (f) => f.externalId && !driveFileIds.has(f.externalId)
  ).length;

  return {
    isSynced: pendingUploads === 0 && pendingDeletes === 0,
    lastSyncAt,
    localCount: localFiles.length,
    driveCount: driveFiles.length,
    pendingUploads,
    pendingDeletes,
  };
}
